/**
 * PORTFOLIO ASSUMPTIONS - CENTRALIZED CONFIGURATION
 *
 * Single source of truth for all financial assumptions used across
 * portfolio calculations. These defaults can be overridden via
 * PortfolioDataContext.assumptions.
 *
 * Usage:
 * - Import DEFAULT_ASSUMPTIONS for base values
 * - Use mergeWithDefaults() to combine user overrides with defaults
 * - All components should read from context, not directly from this file
 */

// ============================================
// DEFAULT ASSUMPTIONS
// ============================================

export const DEFAULT_ASSUMPTIONS = {
  // Loan & Interest
  interestRate: 6.5,           // % p.a.
  loanTermYears: 30,           // years
  loanTermMonths: 360,         // months (loanTermYears * 12)

  // Growth Rates
  capitalGrowthRate: 4.0,      // % p.a.
  rentalGrowthRate: 3.0,       // % p.a.
  inflationRate: 2.5,          // % p.a.

  // Vacancy
  vacancyWeeks: 2,             // weeks per year
  occupancyRate: 96.2,         // % (52 - vacancyWeeks) / 52 * 100

  // Expense Ratios (for quick estimates when actuals not available)
  expenseRatioPercent: 28,     // % of gross rent for total expenses

  // Property Management
  propertyManagementFeePercent: 8.0,  // % of gross rent
  lettingFeeWeeks: 1,                  // weeks of rent per new tenant

  // Maintenance
  maintenancePercentOfValue: 1.0,      // % of property value p.a.
  maintenanceMinimumPA: 2000,          // $ minimum per year
  maintenanceMaximumPA: 10000,         // $ maximum per year

  // Tax Settings (Australian 2024-25)
  marginalTaxRate: 37,         // % - default bracket for investors
  medicareLevy: 2.0,           // %
};

// ============================================
// HOLDING COST DEFAULTS
// ============================================

export const HOLDING_COST_DEFAULTS = {
  // Council Rates (state averages, will be overridden by lookup)
  councilRatesPA: 2000,

  // Water Rates (state averages)
  waterRatesPA: 1200,

  // Insurance (will be calculated based on property value)
  insurancePA: 1800,

  // Miscellaneous
  miscellaneousPA: 1000,
};

// ============================================
// STRATA/BODY CORPORATE FEES BY PROPERTY TYPE
// ============================================

export const STRATA_FEE_DEFAULTS = {
  apartment: { min: 3000, max: 8000, default: 4800 },
  unit: { min: 3000, max: 8000, default: 4500 },
  townhouse: { min: 1500, max: 4000, default: 2400 },
  villa: { min: 1500, max: 4000, default: 2400 },
  house: { min: 0, max: 0, default: 0 },
  land: { min: 0, max: 0, default: 0 },
};

// ============================================
// ACQUISITION COST DEFAULTS
// ============================================

export const ACQUISITION_COST_DEFAULTS = {
  conveyancingFees: 1500,
  buildingAndPestInspection: 700,
  lenderFees: 600,
  depreciationSchedule: 750,
  legalFees: 500,
  searchFees: 200,
};

// ============================================
// TAX BRACKETS (Australian 2024-25)
// ============================================

export const TAX_BRACKETS = [
  { min: 0, max: 18200, rate: 0, base: 0 },
  { min: 18201, max: 45000, rate: 0.16, base: 0 },
  { min: 45001, max: 135000, rate: 0.30, base: 4288 },
  { min: 135001, max: 190000, rate: 0.37, base: 31288 },
  { min: 190001, max: Infinity, rate: 0.45, base: 51638 },
];

// ============================================
// STAMP DUTY BRACKETS BY STATE (2024-25)
// Comprehensive bracket-based calculation
// Source: State Revenue Offices
// ============================================

/**
 * Stamp duty brackets by Australian state
 * Each bracket contains:
 * - min: Minimum value for this bracket (exclusive, except first)
 * - max: Maximum value for this bracket (inclusive)
 * - fromPreviousBracket: Cumulative duty from all previous brackets
 * - rate: Percentage rate for this bracket
 */
export const STAMP_DUTY_BRACKETS = {
  NSW: [
    { min: 0, max: 14000, fromPreviousBracket: 0, rate: 1.25 },
    { min: 14001, max: 31000, fromPreviousBracket: 175, rate: 1.50 },
    { min: 31001, max: 83000, fromPreviousBracket: 430, rate: 1.75 },
    { min: 83001, max: 313000, fromPreviousBracket: 1340, rate: 3.50 },
    { min: 313001, max: 1043000, fromPreviousBracket: 9390, rate: 4.50 },
    { min: 1043001, max: 3505000, fromPreviousBracket: 42240, rate: 5.50 },
    { min: 3505001, max: Infinity, fromPreviousBracket: 177650, rate: 7.00 },
  ],
  VIC: [
    { min: 0, max: 25000, fromPreviousBracket: 0, rate: 1.40 },
    { min: 25001, max: 130000, fromPreviousBracket: 350, rate: 2.40 },
    { min: 130001, max: 960000, fromPreviousBracket: 2870, rate: 6.00 },
    { min: 960001, max: 2000000, fromPreviousBracket: 52670, rate: 5.50 },
    { min: 2000001, max: Infinity, fromPreviousBracket: 109870, rate: 6.50 },
  ],
  QLD: [
    { min: 0, max: 5000, fromPreviousBracket: 0, rate: 0 },
    { min: 5001, max: 75000, fromPreviousBracket: 0, rate: 1.50 },
    { min: 75001, max: 540000, fromPreviousBracket: 1050, rate: 3.50 },
    { min: 540001, max: 1000000, fromPreviousBracket: 17325, rate: 4.50 },
    { min: 1000001, max: Infinity, fromPreviousBracket: 38025, rate: 5.75 },
  ],
  WA: [
    { min: 0, max: 120000, fromPreviousBracket: 0, rate: 1.90 },
    { min: 120001, max: 150000, fromPreviousBracket: 2280, rate: 2.85 },
    { min: 150001, max: 360000, fromPreviousBracket: 3135, rate: 3.80 },
    { min: 360001, max: 725000, fromPreviousBracket: 11115, rate: 4.75 },
    { min: 725001, max: Infinity, fromPreviousBracket: 28453, rate: 5.15 },
  ],
  SA: [
    { min: 0, max: 12000, fromPreviousBracket: 0, rate: 1.00 },
    { min: 12001, max: 30000, fromPreviousBracket: 120, rate: 2.00 },
    { min: 30001, max: 50000, fromPreviousBracket: 480, rate: 3.00 },
    { min: 50001, max: 100000, fromPreviousBracket: 1080, rate: 3.50 },
    { min: 100001, max: 200000, fromPreviousBracket: 2830, rate: 4.00 },
    { min: 200001, max: 250000, fromPreviousBracket: 6830, rate: 4.25 },
    { min: 250001, max: 300000, fromPreviousBracket: 8955, rate: 4.75 },
    { min: 300001, max: 500000, fromPreviousBracket: 11330, rate: 5.00 },
    { min: 500001, max: Infinity, fromPreviousBracket: 21330, rate: 5.50 },
  ],
  TAS: [
    { min: 0, max: 3000, fromPreviousBracket: 50, rate: 0.00 },
    { min: 3001, max: 25000, fromPreviousBracket: 50, rate: 1.75 },
    { min: 25001, max: 75000, fromPreviousBracket: 435, rate: 2.25 },
    { min: 75001, max: 200000, fromPreviousBracket: 1560, rate: 3.50 },
    { min: 200001, max: 375000, fromPreviousBracket: 5935, rate: 4.00 },
    { min: 375001, max: 725000, fromPreviousBracket: 12935, rate: 4.25 },
    { min: 725001, max: Infinity, fromPreviousBracket: 27810, rate: 4.50 },
  ],
  ACT: [
    { min: 0, max: 200000, fromPreviousBracket: 0, rate: 0.60 },
    { min: 200001, max: 300000, fromPreviousBracket: 1200, rate: 2.20 },
    { min: 300001, max: 500000, fromPreviousBracket: 3400, rate: 3.40 },
    { min: 500001, max: 750000, fromPreviousBracket: 10200, rate: 4.32 },
    { min: 750001, max: 1000000, fromPreviousBracket: 21000, rate: 5.90 },
    { min: 1000001, max: 1455000, fromPreviousBracket: 35750, rate: 6.40 },
    { min: 1455001, max: Infinity, fromPreviousBracket: 64870, rate: 4.54 },
  ],
  NT: [
    { min: 0, max: 525000, fromPreviousBracket: 0, rate: 4.95 },
    { min: 525001, max: 3000000, fromPreviousBracket: 26003, rate: 5.75 },
    { min: 3000001, max: 5000000, fromPreviousBracket: 168316, rate: 5.95 },
    { min: 5000001, max: Infinity, fromPreviousBracket: 287316, rate: 5.45 },
  ],
};

// Legacy simple rates (kept for backward compatibility)
export const STAMP_DUTY_RATES = {
  NSW: { rate: 0.045, threshold: 1000000 },
  VIC: { rate: 0.055, threshold: 960000 },
  QLD: { rate: 0.0475, threshold: 1000000 },
  WA: { rate: 0.0515, threshold: 500000 },
  SA: { rate: 0.055, threshold: 500000 },
  TAS: { rate: 0.045, threshold: 400000 },
  ACT: { rate: 0.045, threshold: 500000 },
  NT: { rate: 0.0495, threshold: 525000 },
};

// ============================================
// GOAL CALCULATION DEFAULTS (Unified with CashFlow Engine)
// ============================================

/**
 * Unified defaults for goal/journey planning calculations.
 * These MUST match the CASHFLOW_CONFIG in cashFlowCalculations.js
 * to ensure consistency between Goal Wizard and CashFlow tab.
 *
 * Format follows GoalCalculator.js expectations (decimals not percentages)
 * but values are derived from this file's DEFAULT_ASSUMPTIONS.
 */
export const GOAL_CALCULATION_DEFAULTS = {
  // Property Assumptions
  avgPropertyValue: 600000,                                    // Average acquisition price

  // Growth/Yield (converted to decimals for goal calculator)
  growthRate: DEFAULT_ASSUMPTIONS.capitalGrowthRate / 100,     // 4% -> 0.04
  rentalYield: 0.045,                                          // 4.5% gross yield
  rentalGrowth: DEFAULT_ASSUMPTIONS.rentalGrowthRate / 100,    // 3% -> 0.03

  // Expenses - NOTE: This is overridden by location-aware calculation in enhanced mode
  expenseRatio: 0.30,                                          // 30% of rent for expenses (fallback)

  // Loan Settings (converted to decimals)
  interestRate: DEFAULT_ASSUMPTIONS.interestRate / 100,        // 6.5% -> 0.065
  lvrMax: 0.80,                                                // 80% standard LVR
  loanTerm: DEFAULT_ASSUMPTIONS.loanTermYears,                 // 30 years

  // Transaction Costs (decimals)
  sellingCosts: 0.03,                                          // 3% agent fees
  purchaseCosts: 0.05,                                         // ~5% (fallback - actual uses stamp duty calc)

  // Tax Settings (decimals)
  cgtDiscount: 0.50,                                           // 50% CGT discount if held >12 months
  marginalTaxRate: DEFAULT_ASSUMPTIONS.marginalTaxRate / 100,  // 37% -> 0.37

  // Vacancy
  vacancyWeeks: DEFAULT_ASSUMPTIONS.vacancyWeeks,              // 2 weeks/year

  // Tax-Aware Goal Calculations (NEW)
  // These settings enable more accurate passive income calculations
  defaultInvestorTaxableIncome: 120000,                        // Default taxable income ($120K = 37% bracket)
  taxBenefitServiceabilityFactor: 0.5,                         // Banks may count 50% of tax benefits
  includeDepreciationInTaxCalc: true,                          // Include depreciation in negative gearing calc
  depreciationMethod: 'diminishing_value',                     // ATO-compliant method for P&E
};

/**
 * Convert goal assumptions (decimals) to cashflow assumptions (percentages)
 * Use this when passing goal assumptions to cashflow engine
 */
export function goalToCashflowAssumptions(goalAssumptions) {
  return {
    capitalGrowthRate: (goalAssumptions.growthRate || GOAL_CALCULATION_DEFAULTS.growthRate) * 100,
    rentalGrowthRate: (goalAssumptions.rentalGrowth || GOAL_CALCULATION_DEFAULTS.rentalGrowth) * 100,
    interestRate: (goalAssumptions.interestRate || GOAL_CALCULATION_DEFAULTS.interestRate) * 100,
    vacancyWeeks: goalAssumptions.vacancyWeeks || GOAL_CALCULATION_DEFAULTS.vacancyWeeks,
    loanTerm: goalAssumptions.loanTerm || GOAL_CALCULATION_DEFAULTS.loanTerm,
    inflationRate: DEFAULT_ASSUMPTIONS.inflationRate,
  };
}

/**
 * Convert cashflow assumptions (percentages) to goal assumptions (decimals)
 * Use this when getting assumptions from PortfolioDataContext
 */
export function cashflowToGoalAssumptions(cashflowAssumptions) {
  return {
    ...GOAL_CALCULATION_DEFAULTS,
    growthRate: (cashflowAssumptions.capitalGrowthRate || DEFAULT_ASSUMPTIONS.capitalGrowthRate) / 100,
    rentalGrowth: (cashflowAssumptions.rentalGrowthRate || DEFAULT_ASSUMPTIONS.rentalGrowthRate) / 100,
    interestRate: (cashflowAssumptions.interestRate || DEFAULT_ASSUMPTIONS.interestRate) / 100,
    vacancyWeeks: cashflowAssumptions.vacancyWeeks || DEFAULT_ASSUMPTIONS.vacancyWeeks,
  };
}

// ============================================
// DEPRECIATION DEFAULTS
// ============================================

export const DEPRECIATION_DEFAULTS = {
  buildingWriteOffRate: 2.5,          // % p.a. for buildings post-1985
  plantAndEquipmentYears: 10,          // Average effective life
  estimatedBuildingValuePercent: 60,   // % of property value
  estimatedPnEValuePercent: 10,        // % of property value
  buildingAgeLimit: 40,                // Years (buildings older get $0)
  buildingCutoffYear: 1985,            // Buildings before this get $0 div 43
};

// ============================================
// TRANSFER REGISTRATION FEES BY STATE
// ============================================

/**
 * Transfer (title) registration fees by Australian state
 * These are fees charged by state land registries for property transfers
 */
export const TRANSFER_FEES = {
  NSW: {
    general: 156.55,
    covenant: 312.64,
    easement: 312.64,
  },
  VIC: {
    electronic: 93.40,
    standard: 116.80,
    perThousandAbove1M: 2.34,  // Additional fee per $1,000 above $1M
  },
  QLD: {
    tiers: [
      { max: 180000, fee: 227 },
      { max: 350000, fee: 243 },
      { max: 540000, fee: 259 },
      { max: 1000000, fee: 275 },
      { max: Infinity, fee: 311 },
    ],
  },
  WA: { flat: 187.60 },
  SA: { flat: 177.00 },
  TAS: { flat: 144.72 },
  ACT: { flat: 160.00 },
  NT: { flat: 154.00 },
};

// ============================================
// LENDERS MORTGAGE INSURANCE (LMI) RATES
// ============================================

/**
 * LMI premium rates by Loan-to-Value Ratio (LVR)
 * LMI is required when LVR exceeds 80%
 * Rates are expressed as percentage of loan amount
 */
export const LMI_RATES = [
  { lvr: 80, rate: 0 },       // No LMI at 80% or below
  { lvr: 81, rate: 0.57 },
  { lvr: 82, rate: 0.57 },
  { lvr: 83, rate: 0.70 },
  { lvr: 84, rate: 0.83 },
  { lvr: 85, rate: 0.97 },
  { lvr: 86, rate: 1.08 },
  { lvr: 87, rate: 1.15 },
  { lvr: 88, rate: 1.31 },
  { lvr: 89, rate: 1.62 },
  { lvr: 90, rate: 1.87 },
  { lvr: 91, rate: 2.62 },
  { lvr: 92, rate: 2.67 },
  { lvr: 93, rate: 3.03 },
  { lvr: 94, rate: 3.03 },
  { lvr: 95, rate: 3.35 },
];

// ============================================
// LAND TAX THRESHOLDS BY STATE (2024-25)
// ============================================

/**
 * Land tax thresholds and rates by Australian state
 * Land tax applies to investment properties based on unimproved land value
 * Note: Primary residence (PPOR) is generally exempt
 */
export const LAND_TAX_THRESHOLDS = {
  NSW: {
    threshold: 969000,           // Tax-free threshold for general investors
    premiumThreshold: 5925000,   // Premium rate kicks in above this
    rate: 1.6,                   // % above threshold
    premiumRate: 2.0,            // % above premium threshold
    surcharge: 4.0,              // Foreign investor surcharge %
  },
  VIC: {
    threshold: 50000,            // Very low threshold in VIC
    rate: 0.2,                   // Starting rate %
    brackets: [
      { min: 50000, max: 100000, rate: 0.2 },
      { min: 100000, max: 300000, rate: 0.5 },
      { min: 300000, max: 600000, rate: 0.8 },
      { min: 600000, max: 1000000, rate: 1.3 },
      { min: 1000000, max: 1800000, rate: 1.55 },
      { min: 1800000, max: 3000000, rate: 1.8 },
      { min: 3000000, max: Infinity, rate: 2.55 },
    ],
    surcharge: 2.0,              // Absentee owner surcharge %
  },
  QLD: {
    threshold: 600000,           // Tax-free threshold
    rate: 1.0,                   // % above threshold
    surcharge: 2.0,              // Foreign investor surcharge %
  },
  WA: {
    threshold: 300000,
    rate: 0.25,                  // Starting rate
    brackets: [
      { min: 300000, max: 420000, flat: 300 },
      { min: 420000, max: 1000000, rate: 0.25 },
      { min: 1000000, max: 1800000, rate: 0.9 },
      { min: 1800000, max: 5000000, rate: 1.8 },
      { min: 5000000, max: Infinity, rate: 2.67 },
    ],
  },
  SA: {
    threshold: 450000,
    rate: 0.5,
    brackets: [
      { min: 450000, max: 767000, rate: 0.5 },
      { min: 767000, max: 1085000, rate: 1.65 },
      { min: 1085000, max: Infinity, rate: 2.4 },
    ],
  },
  TAS: {
    threshold: 25000,
    brackets: [
      { min: 25000, max: 350000, rate: 0.55 },
      { min: 350000, max: 750000, rate: 1.5 },
      { min: 750000, max: Infinity, rate: 1.5, plus: 6000 },
    ],
  },
  ACT: {
    // ACT uses a different system - rates/charges instead of land tax
    threshold: 0,
    rate: 0,
    note: 'ACT uses a rates-based system instead of traditional land tax',
  },
  NT: {
    // NT has no land tax
    threshold: Infinity,
    rate: 0,
    note: 'Northern Territory has no land tax',
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Merge user overrides with default assumptions
 * User values take precedence over defaults
 *
 * @param {Object} userAssumptions - User's custom assumptions
 * @returns {Object} Merged assumptions with all required fields
 */
export function mergeWithDefaults(userAssumptions = {}) {
  return {
    ...DEFAULT_ASSUMPTIONS,
    ...userAssumptions,
    // Ensure loanTermMonths stays in sync with loanTermYears
    loanTermMonths: (userAssumptions.loanTermYears || DEFAULT_ASSUMPTIONS.loanTermYears) * 12,
    // Ensure occupancyRate stays in sync with vacancyWeeks
    occupancyRate: ((52 - (userAssumptions.vacancyWeeks || DEFAULT_ASSUMPTIONS.vacancyWeeks)) / 52) * 100,
  };
}

/**
 * Get strata fee for a property type
 *
 * @param {string} propertyType - Type of property (apartment, house, etc.)
 * @param {number|null} actualFee - User-provided actual fee (override)
 * @returns {number} Strata fee per annum
 */
export function getStrataFee(propertyType, actualFee = null) {
  if (actualFee !== null && actualFee !== undefined) {
    return actualFee;
  }

  const normalizedType = propertyType?.toLowerCase() || 'house';
  const fees = STRATA_FEE_DEFAULTS[normalizedType] || STRATA_FEE_DEFAULTS.house;
  return fees.default;
}

/**
 * Calculate estimated maintenance cost
 *
 * @param {number} propertyValue - Current property value
 * @param {Object} assumptions - Assumptions object with maintenance settings
 * @returns {number} Estimated annual maintenance cost
 */
export function calculateMaintenanceEstimate(propertyValue, assumptions = DEFAULT_ASSUMPTIONS) {
  const percentBased = propertyValue * (assumptions.maintenancePercentOfValue / 100);
  return Math.min(
    Math.max(percentBased, assumptions.maintenanceMinimumPA),
    assumptions.maintenanceMaximumPA
  );
}

/**
 * Get marginal tax rate for given taxable income
 *
 * @param {number} taxableIncome - Annual taxable income
 * @returns {number} Marginal tax rate as decimal (e.g., 0.37)
 */
export function getMarginalTaxRate(taxableIncome) {
  for (const bracket of TAX_BRACKETS) {
    if (taxableIncome >= bracket.min && taxableIncome <= bracket.max) {
      return bracket.rate;
    }
  }
  return TAX_BRACKETS[TAX_BRACKETS.length - 1].rate;
}

/**
 * Calculate stamp duty using detailed state brackets
 *
 * @param {string} state - Australian state code (NSW, VIC, QLD, WA, SA, TAS, ACT, NT)
 * @param {number} purchasePrice - Property purchase price
 * @returns {number} Stamp duty amount (rounded to 2 decimal places)
 *
 * @example
 * calculateStampDuty('NSW', 800000) // Returns $31,490
 * calculateStampDuty('VIC', 650000) // Returns $34,070
 */
export function calculateStampDuty(state, purchasePrice) {
  if (!state || !purchasePrice || purchasePrice <= 0) {
    return 0;
  }

  const normalizedState = state.toUpperCase();
  const brackets = STAMP_DUTY_BRACKETS[normalizedState];

  if (!brackets) {
    console.warn(`Unknown state for stamp duty calculation: ${state}`);
    return 0;
  }

  // Find the applicable bracket
  const bracket = brackets.find(b => purchasePrice > b.min && purchasePrice <= b.max);

  if (!bracket) {
    // If price exceeds all brackets, use the last one
    const lastBracket = brackets[brackets.length - 1];
    if (purchasePrice > lastBracket.min) {
      const dutiableValue = purchasePrice - lastBracket.min;
      const duty = lastBracket.fromPreviousBracket + (dutiableValue * lastBracket.rate / 100);
      return Math.round(duty * 100) / 100;
    }
    return 0;
  }

  const { fromPreviousBracket, rate, min } = bracket;
  const dutiableValue = purchasePrice - min;
  const duty = fromPreviousBracket + (dutiableValue * rate / 100);

  return Math.round(duty * 100) / 100;
}

/**
 * Get stamp duty breakdown showing calculation details
 *
 * @param {string} state - Australian state code
 * @param {number} purchasePrice - Property purchase price
 * @returns {Object} Breakdown with duty amount, rate, and bracket info
 */
export function getStampDutyBreakdown(state, purchasePrice) {
  if (!state || !purchasePrice || purchasePrice <= 0) {
    return { duty: 0, effectiveRate: 0, bracket: null, state: null };
  }

  const normalizedState = state.toUpperCase();
  const brackets = STAMP_DUTY_BRACKETS[normalizedState];

  if (!brackets) {
    return { duty: 0, effectiveRate: 0, bracket: null, state: normalizedState };
  }

  const bracket = brackets.find(b => purchasePrice > b.min && purchasePrice <= b.max)
    || brackets[brackets.length - 1];

  const duty = calculateStampDuty(state, purchasePrice);
  const effectiveRate = (duty / purchasePrice) * 100;

  return {
    duty,
    effectiveRate: Math.round(effectiveRate * 100) / 100,
    bracket: {
      min: bracket.min,
      max: bracket.max,
      rate: bracket.rate,
    },
    state: normalizedState,
  };
}

/**
 * Calculate transfer (title registration) fee by state
 *
 * @param {string} state - Australian state code
 * @param {number} propertyValue - Property value
 * @param {Object} options - Optional settings
 * @param {boolean} options.isElectronicLodgement - For VIC: electronic vs standard
 * @param {string} options.nswTransferType - For NSW: 'general', 'covenant', or 'easement'
 * @returns {number} Transfer fee amount
 */
export function calculateTransferFee(state, propertyValue, options = {}) {
  if (!state) return 0;

  const normalizedState = state.toUpperCase();
  const stateData = TRANSFER_FEES[normalizedState];

  if (!stateData) {
    console.warn(`Unknown state for transfer fee: ${state}`);
    return 0;
  }

  switch (normalizedState) {
    case 'VIC': {
      const baseFee = options.isElectronicLodgement ? stateData.electronic : stateData.standard;
      if (propertyValue <= 1000000) {
        return baseFee;
      }
      const excessValue = propertyValue - 1000000;
      const additionalFee = Math.ceil(excessValue / 1000) * stateData.perThousandAbove1M;
      return Math.round((baseFee + additionalFee) * 100) / 100;
    }

    case 'NSW': {
      const transferType = options.nswTransferType?.toLowerCase() || 'general';
      return stateData[transferType] || stateData.general;
    }

    case 'QLD': {
      const tier = stateData.tiers.find(t => propertyValue <= t.max);
      return tier ? tier.fee : stateData.tiers[stateData.tiers.length - 1].fee;
    }

    default:
      return stateData.flat || 0;
  }
}

/**
 * Get LMI rate for a given LVR
 *
 * @param {number} lvr - Loan-to-Value Ratio (e.g., 85 for 85%)
 * @returns {number} LMI rate as percentage (e.g., 0.97 for 0.97%)
 */
export function getLmiRate(lvr) {
  if (lvr <= 80) return 0;

  // Find the rate for the ceiling LVR
  const rate = LMI_RATES.find(r => lvr <= r.lvr);
  return rate ? rate.rate : LMI_RATES[LMI_RATES.length - 1].rate;
}

/**
 * Calculate LMI premium amount
 *
 * @param {number} loanAmount - Total loan amount
 * @param {number} propertyValue - Property value
 * @returns {Object} LMI details including premium, rate, and whether required
 *
 * @example
 * calculateLmi(720000, 800000) // 90% LVR
 * // Returns { premium: 13464, rate: 1.87, lvr: 90, required: true }
 */
export function calculateLmi(loanAmount, propertyValue) {
  if (!loanAmount || !propertyValue || propertyValue <= 0) {
    return { premium: 0, rate: 0, lvr: 0, required: false };
  }

  const lvr = Math.round((loanAmount / propertyValue) * 100);
  const rate = getLmiRate(lvr);
  const required = lvr > 80;
  const premium = required ? Math.round(loanAmount * (rate / 100)) : 0;

  return {
    premium,
    rate,
    lvr,
    required,
  };
}

/**
 * Calculate land tax for a property
 * Note: This is a simplified calculation - actual land tax depends on
 * aggregated land holdings and can be complex
 *
 * @param {string} state - Australian state code
 * @param {number} landValue - Unimproved land value
 * @param {Object} options - Optional settings
 * @param {boolean} options.isForeignOwner - Foreign investor surcharge applies
 * @param {boolean} options.isAbsentee - VIC absentee owner surcharge
 * @returns {Object} Land tax details
 */
export function calculateLandTax(state, landValue, options = {}) {
  if (!state || !landValue || landValue <= 0) {
    return { tax: 0, effectiveRate: 0, threshold: 0, note: null };
  }

  const normalizedState = state.toUpperCase();
  const stateData = LAND_TAX_THRESHOLDS[normalizedState];

  if (!stateData) {
    return { tax: 0, effectiveRate: 0, threshold: 0, note: 'Unknown state' };
  }

  // Handle states with no land tax
  if (stateData.note) {
    return { tax: 0, effectiveRate: 0, threshold: stateData.threshold, note: stateData.note };
  }

  let tax = 0;

  // Simple threshold-based calculation for NSW, QLD
  if (normalizedState === 'NSW') {
    if (landValue <= stateData.threshold) {
      tax = 0;
    } else if (landValue <= stateData.premiumThreshold) {
      tax = (landValue - stateData.threshold) * (stateData.rate / 100);
    } else {
      const standardTax = (stateData.premiumThreshold - stateData.threshold) * (stateData.rate / 100);
      const premiumTax = (landValue - stateData.premiumThreshold) * (stateData.premiumRate / 100);
      tax = standardTax + premiumTax;
    }
    if (options.isForeignOwner) {
      tax += landValue * (stateData.surcharge / 100);
    }
  } else if (normalizedState === 'QLD') {
    if (landValue > stateData.threshold) {
      tax = (landValue - stateData.threshold) * (stateData.rate / 100);
    }
    if (options.isForeignOwner) {
      tax += landValue * (stateData.surcharge / 100);
    }
  } else if (stateData.brackets) {
    // Bracket-based calculation for VIC, WA, SA, TAS
    for (const bracket of stateData.brackets) {
      if (landValue > bracket.min && landValue <= bracket.max) {
        if (bracket.flat) {
          tax = bracket.flat;
        } else {
          tax = (landValue - bracket.min) * (bracket.rate / 100);
          if (bracket.plus) {
            tax += bracket.plus;
          }
        }
        break;
      }
    }
    if (normalizedState === 'VIC' && options.isAbsentee) {
      tax += landValue * (stateData.surcharge / 100);
    }
  }

  const effectiveRate = landValue > 0 ? (tax / landValue) * 100 : 0;

  return {
    tax: Math.round(tax),
    effectiveRate: Math.round(effectiveRate * 100) / 100,
    threshold: stateData.threshold,
    note: null,
  };
}

/**
 * Calculate borrowable equity from a property
 * Borrowable equity = (Current Value × 80%) - Current Loan
 *
 * @param {number} currentValue - Current property value
 * @param {number} currentLoan - Current loan amount
 * @param {number} maxLvr - Maximum LVR (default 80%)
 * @returns {Object} Equity details
 */
export function calculateBorrowableEquity(currentValue, currentLoan, maxLvr = 80) {
  if (!currentValue || currentValue <= 0) {
    return { borrowableEquity: 0, totalEquity: 0, currentLvr: 0, maxBorrowable: 0 };
  }

  const loan = currentLoan || 0;
  const maxBorrowable = currentValue * (maxLvr / 100);
  const borrowableEquity = Math.max(0, maxBorrowable - loan);
  const totalEquity = currentValue - loan;
  const currentLvr = (loan / currentValue) * 100;

  return {
    borrowableEquity: Math.round(borrowableEquity),
    totalEquity: Math.round(totalEquity),
    currentLvr: Math.round(currentLvr * 100) / 100,
    maxBorrowable: Math.round(maxBorrowable),
  };
}

/**
 * Calculate Principal & Interest loan repayment
 * Uses standard amortization formula
 *
 * @param {number} loanAmount - Total loan amount
 * @param {number} annualInterestRate - Annual interest rate (e.g., 6.5 for 6.5%)
 * @param {number} loanTermYears - Loan term in years
 * @returns {Object} Repayment details including monthly/annual payments and schedule
 */
export function calculatePrincipalInterestRepayment(loanAmount, annualInterestRate, loanTermYears) {
  if (!loanAmount || !annualInterestRate || !loanTermYears) {
    return {
      monthlyPayment: 0,
      annualPayment: 0,
      totalInterest: 0,
      totalRepayment: 0,
      yearlySchedule: [],
    };
  }

  const monthlyRate = annualInterestRate / 100 / 12;
  const totalPayments = loanTermYears * 12;

  // Standard P&I formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
  const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
    (Math.pow(1 + monthlyRate, totalPayments) - 1);

  let balance = loanAmount;
  const yearlySchedule = [];
  let totalInterest = 0;

  for (let year = 1; year <= loanTermYears; year++) {
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;

    for (let month = 1; month <= 12; month++) {
      const monthlyInterest = balance * monthlyRate;
      const monthlyPrincipal = monthlyPayment - monthlyInterest;

      yearlyInterest += monthlyInterest;
      yearlyPrincipal += monthlyPrincipal;
      balance = Math.max(0, balance - monthlyPrincipal);
    }

    totalInterest += yearlyInterest;

    yearlySchedule.push({
      year,
      annualRepayment: Math.round(monthlyPayment * 12),
      annualPrincipal: Math.round(yearlyPrincipal),
      annualInterest: Math.round(yearlyInterest),
      remainingBalance: Math.round(balance),
    });
  }

  return {
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    annualPayment: Math.round(monthlyPayment * 12),
    totalInterest: Math.round(totalInterest),
    totalRepayment: Math.round(loanAmount + totalInterest),
    yearlySchedule,
  };
}

/**
 * Calculate Interest-Only loan repayment
 *
 * @param {number} loanAmount - Total loan amount
 * @param {number} annualInterestRate - Annual interest rate (e.g., 6.5 for 6.5%)
 * @returns {Object} Repayment details
 */
export function calculateInterestOnlyRepayment(loanAmount, annualInterestRate) {
  if (!loanAmount || !annualInterestRate) {
    return { monthlyPayment: 0, annualPayment: 0 };
  }

  const annualInterest = loanAmount * (annualInterestRate / 100);
  const monthlyPayment = annualInterest / 12;

  return {
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    annualPayment: Math.round(annualInterest),
  };
}

/**
 * Compare P&I vs Interest-Only repayments
 *
 * @param {number} loanAmount - Total loan amount
 * @param {number} annualInterestRate - Annual interest rate
 * @param {number} loanTermYears - Loan term in years
 * @returns {Object} Comparison of both repayment types
 */
export function compareLoanRepayments(loanAmount, annualInterestRate, loanTermYears) {
  const pi = calculatePrincipalInterestRepayment(loanAmount, annualInterestRate, loanTermYears);
  const io = calculateInterestOnlyRepayment(loanAmount, annualInterestRate);

  const monthlyDifference = pi.monthlyPayment - io.monthlyPayment;
  const annualDifference = pi.annualPayment - io.annualPayment;

  return {
    principalAndInterest: {
      monthlyPayment: pi.monthlyPayment,
      annualPayment: pi.annualPayment,
      totalInterest: pi.totalInterest,
      totalRepayment: pi.totalRepayment,
      loanPaidOff: true,
    },
    interestOnly: {
      monthlyPayment: io.monthlyPayment,
      annualPayment: io.annualPayment,
      totalInterest: io.annualPayment * loanTermYears,
      totalRepayment: loanAmount + (io.annualPayment * loanTermYears),
      loanPaidOff: false,
      remainingBalance: loanAmount,
    },
    comparison: {
      monthlyDifference: Math.round(monthlyDifference * 100) / 100,
      annualDifference: Math.round(annualDifference),
      interestSavings: Math.round((io.annualPayment * loanTermYears) - pi.totalInterest),
    },
  };
}

/**
 * Validate assumptions object has required fields
 * Returns list of missing/invalid fields
 *
 * @param {Object} assumptions - Assumptions to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export function validateAssumptions(assumptions) {
  const errors = [];
  const required = ['interestRate', 'loanTermYears', 'capitalGrowthRate'];

  for (const field of required) {
    if (assumptions[field] === undefined || assumptions[field] === null) {
      errors.push(`Missing required field: ${field}`);
    }
  }

  if (assumptions.interestRate < 0 || assumptions.interestRate > 25) {
    errors.push('Interest rate must be between 0 and 25%');
  }

  if (assumptions.loanTermYears < 1 || assumptions.loanTermYears > 40) {
    errors.push('Loan term must be between 1 and 40 years');
  }

  return errors;
}

// ============================================
// GOAL ONBOARDING CONFIGURATION
// Used by GoalOnboardingWizard and related components
// ============================================

export const GOAL_CONFIG = {
  // Projection assumptions for goal calculations
  projections: {
    avgPropertyValue: 600000,    // $ average property value for calculations
    avgGrossYield: 5.5,          // % gross rental yield
    avgNetYield: 3.5,            // % net yield after expenses
    avgGrowthRate: 4.0,          // % p.a. capital growth (aligned with DEFAULT_ASSUMPTIONS)
    loanFreeYield: 4.5,          // % yield when property is debt-free
  },

  // Income presets for passive income goal selection
  incomePresets: [
    { value: 50000, label: '$50K', description: 'Part-time freedom' },
    { value: 80000, label: '$80K', description: 'Median income replacement' },
    { value: 100000, label: '$100K', description: 'Full income replacement' },
    { value: 150000, label: '$150K', description: 'Premium lifestyle' },
  ],

  // Income slider configuration
  incomeRange: {
    min: 30000,
    max: 200000,
    step: 10000,
  },

  // Starting capital options
  capitalOptions: [
    { value: 50000, label: '$50K' },
    { value: 100000, label: '$100K' },
    { value: 150000, label: '$150K' },
    { value: 200000, label: '$200K+' },
  ],

  // PPOR balance quick select presets
  pporBalancePresets: [300000, 400000, 500000, 600000, 750000],

  // Timeline options with confidence levels
  timelineOptions: {
    7: { label: '7 Years', pace: 'Aggressive', risk: 'Higher', confidence: 65 },
    10: { label: '10 Years', pace: 'Focused', risk: 'Moderate', confidence: 75 },
    12: { label: '12 Years', pace: 'Balanced', risk: 'Balanced', confidence: 85 },
    15: { label: '15 Years', pace: 'Steady', risk: 'Lower', confidence: 90 },
  },

  // Goal type definitions
  // Note: iconType is used by components to render Lucide icons
  goalTypes: {
    ppor_payoff: {
      id: 'ppor_payoff',
      icon: '🏠',
      iconType: 'home',
      title: 'Pay Off My Home',
      subtitle: 'Own your home outright',
      description: 'Build equity through property to pay off your home faster',
      focus: 'Capital Growth',
      typicalTimeline: '5-10 years',
      color: '#3B82F6',
    },
    passive_income: {
      id: 'passive_income',
      icon: '💰',
      iconType: 'wallet',
      title: 'Passive Income',
      subtitle: 'Replace your salary',
      description: 'Build a portfolio that generates consistent cash flow',
      focus: 'Cash Flow',
      typicalTimeline: 'Ongoing',
      color: '#22C55E',
    },
    true_freedom: {
      id: 'true_freedom',
      icon: '🎯',
      iconType: 'trophy',
      title: 'True Freedom',
      subtitle: 'Complete financial independence',
      description: 'Full financial independence - income without debt obligations',
      focus: 'Full Journey',
      typicalTimeline: '10-15 years',
      color: '#F5B741',
    },
  },

  // Validation thresholds
  validation: {
    pporBalanceMin: 50000,       // Minimum PPOR balance to be realistic
    targetIncomeMin: 20000,      // Minimum passive income target
    startingCapitalMin: 10000,   // Minimum starting capital
    timelineMin: 5,              // Minimum years
    timelineMax: 25,             // Maximum years
  },
};

// ============================================
// EXPORT COMBINED CONFIG (for backward compatibility)
// ============================================

export const PORTFOLIO_CONFIG = {
  defaults: DEFAULT_ASSUMPTIONS,
  holdingCosts: HOLDING_COST_DEFAULTS,
  strataFees: STRATA_FEE_DEFAULTS,
  acquisitionCosts: ACQUISITION_COST_DEFAULTS,
  taxBrackets: TAX_BRACKETS,
  stampDutyBrackets: STAMP_DUTY_BRACKETS,
  stampDutyRates: STAMP_DUTY_RATES, // Legacy - kept for backward compatibility
  transferFees: TRANSFER_FEES,
  lmiRates: LMI_RATES,
  landTaxThresholds: LAND_TAX_THRESHOLDS,
  depreciation: DEPRECIATION_DEFAULTS,
  goalConfig: GOAL_CONFIG,
};

export default PORTFOLIO_CONFIG;
